import { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { fmt, apiError } from "@/lib/api";
import { PageHeader, Loader } from "@/components/ui-bits";
import { Sparkles, Clock, Check, Loader2, Wallet, Search } from "lucide-react";

export default function MagicCards() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [buying, setBuying] = useState(false);
  const [cardQuery, setCardQuery] = useState("");

  const load = () => API.get("/magic-cards").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);

  const use = async () => {
    setBusy(true);
    try {
      const r = await API.post("/magic-cards/use");
      toast.success(r.data.message);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const buy = async () => {
    setBuying(true);
    try {
      const r = await API.post("/magic-cards/buy");
      toast.success(r.data.message);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBuying(false); }
  };

  if (!d) return <Loader label="Loading magic cards" />;
  const usable = d.cards.filter((c) => c.usable).length;
  const nextDrop = d.next_drop_available_at ? new Date(d.next_drop_available_at) : null;
  const buyable = Math.floor((d.magic_card_wallet || 0) / (d.buy_price || 1));
  const lv = d.level_values || [0.05, 0.05, 0.15];
  const dropped = d.dropped_cards || [];
  const q = cardQuery.trim();
  const filteredDropped = q ? dropped.filter((r) => String(r.card_no) === q) : dropped;

  return (
    <div>
      <PageHeader
        title="Magic Cards"
        subtitle={`You can drop only 1 card in every ${d.cooldown_hours} hours.`}
        testid="cards-header"
        right={
          <button data-testid="use-card-btn" disabled={busy || !d.can_drop_now} onClick={use}
            className="inline-flex items-center gap-2 bg-violet-500 hover:bg-violet-400 disabled:opacity-50 text-white font-semibold px-5 py-2.5 rounded-xl">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} <Sparkles className="h-4 w-4" /> Drop a Card
          </button>
        }
      />

      <div className="glass rounded-2xl p-6 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="magic-wallet-buy">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl grid place-items-center bg-violet-500/15"><Wallet className="h-5 w-5 text-violet-300" /></div>
          <div>
            <div className="text-[0.7rem] uppercase font-mono tracking-[0.18em] text-slate-400">Magic Card Wallet</div>
            <div className="font-num text-2xl font-bold text-white" data-testid="magic-wallet-balance">{fmt(d.magic_card_wallet)}</div>
            <p className="text-xs text-slate-500 mt-0.5">1 Magic Card = ${d.buy_price}. Buy converts your whole-dollar balance into cards.</p>
          </div>
        </div>
        <button data-testid="buy-card-btn" disabled={buying || buyable < 1} onClick={buy}
          className="shrink-0 inline-flex items-center justify-center gap-2 bg-violet-500 hover:bg-violet-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-5 py-2.5 rounded-xl">
          {buying && <Loader2 className="h-4 w-4 animate-spin" />} <Sparkles className="h-4 w-4" /> {buyable >= 1 ? `Buy ${buyable} Card${buyable > 1 ? "s" : ""}` : "Buy Cards"}
        </button>
      </div>

      {!d.can_drop_now && nextDrop && d.used < d.total && (
        <div className="glass rounded-2xl p-4 mb-6 flex items-center gap-3 text-sm text-amber-300">
          <Clock className="h-5 w-5" /> Next card can be dropped at {nextDrop.toLocaleString()}.
        </div>
      )}

      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="glass rounded-2xl p-5"><div className="text-xs uppercase tracking-wide text-slate-500">Total</div><div className="font-num text-3xl font-bold text-white mt-1">{d.total}</div></div>
        <div className="glass rounded-2xl p-5"><div className="text-xs uppercase tracking-wide text-slate-500">Ready to drop</div><div className="font-num text-3xl font-bold text-emerald-400 mt-1">{d.can_drop_now ? 1 : 0}</div></div>
        <div className="glass rounded-2xl p-5"><div className="text-xs uppercase tracking-wide text-slate-500">Dropped</div><div className="font-num text-3xl font-bold text-slate-400 mt-1">{d.used}</div></div>
      </div>

      {d.cards.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center text-slate-500">Activate a package to receive Magic Cards.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {d.cards.map((c) => {
            const used = !!c.used_at;
            return (
              <div key={c.id} data-testid={`magic-card-${c.id}`}
                className={`rounded-2xl p-4 border relative overflow-hidden aspect-[3/4] flex flex-col justify-between ${
                  used ? "border-white/5 bg-white/[0.02] opacity-50"
                  : c.usable ? "border-violet-500/40 bg-gradient-to-br from-violet-500/20 to-cyan-500/10"
                  : "border-amber-500/20 bg-amber-500/[0.05]"}`}>
                <Sparkles className={`h-6 w-6 ${used ? "text-slate-500" : c.usable ? "text-violet-300" : "text-amber-400"}`} />
                <div>
                  <div className="font-semibold text-sm text-white">Magic Card</div>
                  <div className="text-[0.65rem] mt-1 flex items-center gap-1 text-slate-400">
                    {used ? <><Check className="h-3 w-3" /> Dropped</> :
                     c.usable ? <span className="text-violet-300">Ready</span> :
                     <><Clock className="h-3 w-3" /> Cooldown</>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {dropped.length > 0 && (
        <div className="glass rounded-2xl p-4 sm:p-6 mt-8" data-testid="dropped-earnings">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h3 className="font-display text-lg font-semibold text-white">Your Dropped Cards — Earnings</h3>
              <p className="text-xs text-slate-500 mt-1">Each dropped card earns as your matrix fills below it: L1 {fmt(lv[0])}, L2 {fmt(lv[1])}, L3 {fmt(lv[2])} per box. Search a card number to check its payment.</p>
            </div>
            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3">
              <Search className="h-4 w-4 text-slate-500" />
              <input data-testid="card-no-search" type="number" min="1" value={cardQuery} onChange={(e) => setCardQuery(e.target.value)}
                placeholder="Card # (e.g. 9)" className="w-32 bg-transparent text-white outline-none py-2 font-num" />
              {q && <button data-testid="card-no-clear" onClick={() => setCardQuery("")} className="text-xs text-slate-400 hover:text-white">Clear</button>}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-white/10">
                <th className="py-2 pr-4">Card #</th><th className="py-2 pr-4">Dropped</th>
                <th className="py-2 pr-4">L1 / L2 / L3 filled</th><th className="py-2 pr-4">Earned</th><th className="py-2 text-right">Payment</th>
              </tr></thead>
              <tbody>
                {filteredDropped.length === 0 ? (
                  <tr><td colSpan="5" className="py-8 text-center text-slate-500" data-testid="card-not-found">No card #{q} found. You have dropped {dropped.length} card(s).</td></tr>
                ) : filteredDropped.map((r) => (
                  <tr key={r.drop_id} data-testid={`dropped-card-row-${r.card_no}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                    <td className="py-3 pr-4 font-num text-white">#{r.card_no}</td>
                    <td className="py-3 pr-4 font-num text-slate-400">{r.dropped_at ? new Date(r.dropped_at).toLocaleString() : "—"}</td>
                    <td className="py-3 pr-4 font-num text-slate-300">{r.level_counts[0]} / {r.level_counts[1]} / {r.level_counts[2]}</td>
                    <td className="py-3 pr-4 font-num text-emerald-300">{fmt(r.earned)}</td>
                    <td className="py-3 text-right">
                      {r.paid
                        ? <span data-testid={`card-paid-${r.card_no}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"><Check className="h-3 w-3" /> Received</span>
                        : <span data-testid={`card-pending-${r.card_no}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-slate-500/15 text-slate-400 border border-white/10"><Clock className="h-3 w-3" /> Awaiting downline</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
